import { SortAscendingOutlineIcon, SortDescendingOutlineIcon } from '@applyboard/ui-icons';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Header,
  Row,
  RowData,
  ColumnSort,
  useReactTable,
  SortingState,
  ColumnDefTemplate,
  HeaderContext,
} from '@tanstack/react-table';
import lodashGet from 'lodash/get';
import React, { useState } from 'react';
import {
  ActionsCell,
  ActionsCellProps,
  BooleanCell,
  BooleanCellProps,
  Cell,
  CellProps,
  CountryCell,
  CountryCellProps,
  DateCell,
  DateCellProps,
  EmailCell,
  EmailCellProps,
  ExpandCell,
  ExpandCellProps,
  TextCell,
  TextCellProps,
} from './Table.cells';
import { Flex } from '../Flex';
import { Pagination, PaginationProps } from '../Pagination';
import { Spinner } from '../Spinner';
import { Text } from '../Text';
import { useFullTheme } from '~/design';
import { findChildOfType } from '~/utils';
import { IconWrapper } from '~/internal';

export interface TableColumn<T> {
  accessorKey: string;
  cell?:
    | ColumnDef<T>['cell']
    | typeof Table.BooleanCell
    | typeof Table.CountryCell
    | typeof Table.DateCell
    | typeof Table.EmailCell
    | typeof Table.ExpandCell
    | typeof Table.TextCell
    | React.ComponentType<T>;
  cellSpansEntireTable?: (props: T) => boolean;
  divider?: boolean;
  enableMultiSort?: ColumnDef<T>['enableMultiSort'];
  enableSorting?: ColumnDef<T>['enableSorting'];
  pinned?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  header: ColumnDefTemplate<HeaderContext<T, any>>;
  width?: 'content';
}
export interface TableSubComponentMeta {
  numColumns: number;
}

export interface TableRenderSubComponentResult {
  content: React.ReactNode;
  divider?: boolean;
  offset?: number;
  width: number;
}

export interface TableProps<T> {
  children?: React.ReactNode;

  /** If there's text on the page that could label the table, pass its ID to
   * this prop to improve a11y */
  'aria-labelledby'?: string;

  /** An object containing parameters for all the table columns.
   * Refer to the `TableColumn` type for the available props. */
  columns: TableColumn<T>[];

  /** Data that will be rendered on the columns that were defined in the `columns` prop. */
  data: T[];

  /** Sets the rows that are expanded by default when the table is rendered */
  expanded?: number[];

  /** Shows a loading spinner on top of the table cells when data is loading.  */
  loading?: boolean;

  /** Callback function to run when a row is expanded or collapsed. Passing this prop will make
   * the `expanded` prop into a controlled value */
  onExpand?: (expandedRows: number[]) => void;

  /** Callback function to run when column sorting happens */
  onSort?: (sorting: TableColumnSort) => void;

  /** How the table behaves when it exceeds the max-width */
  overflow?: 'scroll';

  /** Component function to render for expandable rows */
  renderSubComponent?: (data: T, meta: TableSubComponentMeta) => TableRenderSubComponentResult;

  /** The sorting for the table. If you pass this prop, you are responsible for ensuring the sorting
   * value is kept in sync via the `onSort` prop. */
  sort?: TableColumnSort;

  /** The allowed sort options for the table. Right now the only allowed value will force the
   * table to be sorted on initial render, and allow users to click through sorting to explicitly
   * set the column as unsorted. */
  sortOptions?: ['asc', 'desc'];
}

export type TableColumnSort = ColumnSort[];

/**
 * Table serves to display data on a structured way. It consists of rows and columns arranged in a
 * grid-like pattern. Each intersection of a row and a column is called a cell.
 */
export const Table = <T extends RowData>({
  'aria-labelledby': ariaLabelledBy,
  children,
  columns: _columns,
  data,
  expanded: _expanded,
  loading = false,
  onExpand,
  onSort,
  overflow,
  renderSubComponent,
  sort: controlledSort = undefined,
  sortOptions,
}: TableProps<T>) => {
  const [sorting, setSorting] = React.useState<TableColumnSort>(controlledSort ?? []);
  const theme = useFullTheme();
  const paginationProps = (
    findChildOfType(
      children,
      Table.Pagination,
    ) as unknown as React.ReactElement<TablePaginationProps>
  )?.props;
  const noDataChild = findChildOfType(children, Table.NoData) as React.ReactElement<NoDataProps>;

  const [expandedState, setExpandedState] = useState(getExpandedValue(_expanded));
  const expanded = onExpand ? getExpandedValue(_expanded) : expandedState;

  const columns = React.useMemo(
    () =>
      _columns.map(column => ({
        accessorKey: column.accessorKey,
        cell: ({ row }: { row: Row<T> }) => {
          const renderCellElement = column.cell as (props: unknown) => React.ReactElement;
          const defaultData = lodashGet(
            row.original as { [key: string]: string | boolean },
            column.accessorKey,
          );

          return !column.cell || column.cell === Table.TextCell ? (
            <TextCell>{defaultData as string}</TextCell>
          ) : column.cell === Table.BooleanCell ? (
            <BooleanCell value={defaultData as boolean} />
          ) : column.cell === Table.CountryCell ? (
            <CountryCell value={defaultData as string} />
          ) : column.cell === Table.DateCell ? (
            <DateCell value={defaultData as string} />
          ) : column.cell === Table.EmailCell ? (
            <EmailCell value={defaultData as string} />
          ) : column.cell === Table.ExpandCell ? (
            <ExpandCell
              getCanExpand={row.getCanExpand}
              getIsExpanded={row.getIsExpanded}
              getToggleExpandedHandler={row.getToggleExpandedHandler}
            />
          ) : (
            renderCellElement(row.original)
          );
        },
        cellSpansEntireTable: column.cellSpansEntireTable,
        divider: column.divider,
        enableSorting: column.enableSorting ?? false,
        enableMultiSort: column.enableMultiSort ?? false,
        header: column.header,
        pinned: column.pinned,
        width: column.width,
      })),
    [_columns],
  );

  const columnPinning = React.useMemo(() => {
    const result = columns.reduce((subresult, column) => {
      if (column.pinned) {
        subresult.push(column.accessorKey);
      }
      return subresult;
    }, [] as string[]);

    if (Object.keys(result).length > 1) {
      throw new Error('At this time, Table only supports 1 pinned column.');
    }

    return result;
  }, [columns]);

  const table = useReactTable<T>({
    columns,
    data,
    defaultColumn: {
      enableSorting: false,
      enableMultiSort: false,
      minSize: 0,
      size: 0,
    },
    debugTable: false,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => !!renderSubComponent,
    manualPagination: true,
    manualSorting: true,
    onExpandedChange: value => {
      if (onExpand) {
        const expandedObj = typeof value === 'function' ? value(expanded) : undefined;
        if (expandedObj) {
          onExpand(Object.keys(expandedObj).map(Number));
        }
      } else {
        // @ts-expect-error value is an updater function, e.g. `(oldValue) => newValue` which is
        // a valid value for a setState function
        setExpandedState(value);
      }
    },
    state: {
      columnPinning: { left: columnPinning },
      expanded,
      pagination: paginationProps
        ? {
            pageIndex: paginationProps.currentPage,
            pageSize: paginationProps.pageSize,
          }
        : undefined,
      sorting,
    },
  });

  const onSortHelper = React.useCallback(
    (newValue: SortingState) => {
      onSort?.(newValue);
      if (typeof controlledSort === 'undefined') {
        setSorting(newValue);
      }
    },
    [controlledSort, onSort],
  );

  if (sorting !== controlledSort && typeof controlledSort !== 'undefined') {
    setSorting(controlledSort);
  }

  if (sortOptions && !sorting.length) {
    throw new Error('[Table] At least one column must be sorted.');
  }

  return (
    <div>
      <div css={[overflow === 'scroll' && { overflowX: 'auto' }]}>
        <table
          css={{
            borderSpacing: 0,
            height: '1px',
            width: '100%',
          }}
          aria-labelledby={ariaLabelledBy}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHeader
                    header={header}
                    key={header.id}
                    setSorting={onSortHelper}
                    sorting={sorting}
                    sortOptions={sortOptions}
                  />
                ))}
              </tr>
            ))}
          </thead>

          <tbody css={{ position: 'relative' }}>
            {data.length === 0 && loading ? (
              <Placeholder numColumns={columns.length}>
                <Spinner />
              </Placeholder>
            ) : data.length === 0 && !loading ? (
              <Placeholder numColumns={columns.length}>
                {noDataChild ? <Table.NoData {...noDataChild.props} /> : <Text>No results.</Text>}
              </Placeholder>
            ) : (
              table.getRowModel().rows.map(row => {
                const numRows = table.getRowModel().rows.length;
                const border = theme.borders.inactive;
                const isExpanded = row.getIsExpanded();
                const renderSubcomponentResults =
                  isExpanded && renderSubComponent
                    ? renderSubComponent(row.original, { numColumns: row.getVisibleCells().length })
                    : null;
                const borderBottomStyle = {
                  borderBottomColor: border.borderColor,
                  borderBottomStyle: border.borderStyle,
                  borderBottomWidth: border.borderWidth,
                };
                const borderRightStyle = {
                  borderRightColor: border.borderColor,
                  borderRightStyle: border.borderStyle,
                  borderRightWidth: border.borderWidth,
                };
                const isLastRow = row.index === numRows - 1;

                let isSingleCellRow = false;
                const cells = row.getVisibleCells();
                return (
                  <React.Fragment key={row.id}>
                    <tr>
                      {cells.map(cell => {
                        if (isSingleCellRow) {
                          return null;
                        }

                        const columnDef = cell.column.columnDef as TableColumn<T>;
                        const width =
                          columnDef.width === 'content'
                            ? 0
                            : cell.column.getSize() !== 0
                              ? cell.column.getSize()
                              : undefined;

                        const cellSpansEntireTable = columnDef.cellSpansEntireTable?.(
                          cell.row.original,
                        );
                        if (cellSpansEntireTable) {
                          isSingleCellRow = true;
                        }

                        return (
                          <td
                            css={[
                              {
                                backgroundColor: theme.colors.bgRegular,
                                margin: 0,
                                padding: '0 !important',
                                width,
                              },
                              row.index !== 0 && {
                                borderTopColor: border.borderColor,
                                borderTopStyle: border.borderStyle,
                                borderTopWidth: border.borderWidth,
                              },
                              isLastRow && !isExpanded && borderBottomStyle,
                              columnDef.divider && borderRightStyle,
                              columnDef.pinned && {
                                left: cell.column.getStart('left'),
                                position: 'sticky',
                              },
                            ]}
                            key={cell.id}
                            colSpan={cellSpansEntireTable ? cells.length : undefined}
                          >
                            {flexRender(columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>

                    {!!renderSubcomponentResults && (
                      <tr>
                        {renderSubcomponentResults.offset && (
                          <td
                            colSpan={renderSubcomponentResults.offset}
                            css={[
                              isLastRow && borderBottomStyle,
                              renderSubcomponentResults.divider && borderRightStyle,
                            ]}
                          ></td>
                        )}
                        <td
                          colSpan={renderSubcomponentResults.width}
                          css={isLastRow && borderBottomStyle}
                        >
                          {renderSubcomponentResults.content}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}

            {data.length !== 0 && loading && (
              <tr>
                <td>
                  <LoadingOverlay />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!!paginationProps && data.length !== 0 && (
        <Flex justify="center" pt={6}>
          <Flex.Item grow={paginationProps.variant === 'items' ? 1 : 0}>
            <Pagination
              currentPage={paginationProps.currentPage}
              disabled={loading}
              numItems={paginationProps.numItems}
              onPageChange={paginationProps.onPageChange}
              onPageSizeChange={paginationProps.onPageSizeChange}
              pageSize={paginationProps.pageSize}
              pageSizeOptions={paginationProps.pageSizeOptions}
              variant={paginationProps.variant}
            />
          </Flex.Item>
        </Flex>
      )}
    </div>
  );
};

function TableHeader<T>({
  header,
  setSorting,
  sorting,
  sortOptions,
}: {
  header: Header<T, unknown>;
  setSorting: (sorting: SortingState) => void;
  sorting: SortingState;
  sortOptions?: TableProps<T>['sortOptions'];
}) {
  const theme = useFullTheme();
  const table = header.getContext().table;
  const column = header.column;
  const canSort = column.getCanSort() || column.getCanMultiSort();
  const sortDirection = canSort ? column.getIsSorted() : null;

  const ContainerTag = canSort ? 'button' : React.Fragment;
  const containerTagProps = canSort
    ? {
        css: {
          alignItems: 'center',
          backgroundColor: 'transparent',
          border: 0,
          cursor: 'pointer',
          display: 'flex',
          gap: theme.spacing[4],
          margin: 0,
          padding: 0,
        },
        onClick: () => {
          const validSortOptions = sortOptions ?? (['asc', 'desc', 'none'] as const);
          const oldDirection = sortDirection ?? 'none';
          const idx = validSortOptions.findIndex(dir => dir === oldDirection);
          const newDirection = validSortOptions[(idx + 1) % validSortOptions.length];

          if (!newDirection || newDirection === 'none') {
            setSorting?.(sorting.filter(h => h.id !== column.id));
          } else if (newDirection === 'asc') {
            const newSortEntry = { id: column.id, desc: false };
            const columns = table.getAllColumns();
            const newValue =
              // If this column can multisort, then it can be combined with whatever is already
              // being sorted, unless what's being sorted is not meant to be multisorted
              column.getCanMultiSort()
                ? [
                    ...(sorting.filter(sortEntry => sortEntry.id !== newSortEntry.id) ?? []),
                    newSortEntry,
                  ].filter(sortEntry => {
                    const column = columns.find(c => c.id === sortEntry.id);
                    return !!column && (column.getCanMultiSort() ?? false);
                  })
                : [newSortEntry];

            setSorting?.(newValue);
          } else if (newDirection === 'desc') {
            const newValue = sorting.slice();
            newValue.splice(
              newValue.findIndex(s => s.id === column.id),
              1,
              {
                id: column.id,
                desc: true,
              },
            );
            setSorting?.(newValue);
          }
        },
      }
    : {};

  const SortIcon =
    sortDirection === 'asc'
      ? SortAscendingOutlineIcon
      : sortDirection === 'desc'
        ? SortDescendingOutlineIcon
        : null;

  const width = header.getSize() !== 0 ? header.getSize() : undefined;
  const columnDef = column.columnDef as TableColumn<T>;

  return (
    <th
      aria-sort={
        sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : undefined
      }
      colSpan={header.colSpan}
      css={[
        {
          backgroundColor: theme.colors.bgRegular,
          borderBottomColor: theme.borders.inactive.borderColor,
          borderBottomStyle: theme.borders.inactive.borderStyle,
          borderBottomWidth: theme.borders.inactive.borderWidth,
          padding: `${theme.spacing[4]} !important`, // legacy.min.css has !important
          textAlign: 'left',
          textWrap: 'nowrap',
          width,
        },
        columnDef.divider && {
          borderRightColor: theme.borders.inactive.borderColor,
          borderRightStyle: theme.borders.inactive.borderStyle,
          borderRightWidth: theme.borders.inactive.borderWidth,
        },
        columnDef.pinned && {
          left: column.getStart('left'),
          position: 'sticky',
        },
      ]}
      key={header.id}
    >
      {header.isPlaceholder ? null : (
        <ContainerTag {...containerTagProps}>
          <Text overflow="nowrap" variant="labelM" contrast="mid">
            {flexRender(column.columnDef.header, header.getContext())}
          </Text>

          {SortIcon && (
            <IconWrapper
              allowInteractions={canSort}
              intent="secondary"
              selector={canSort ? 'button' : 'th'}
            >
              <SortIcon size="sm" />
            </IconWrapper>
          )}
        </ContainerTag>
      )}
    </th>
  );
}

function Placeholder({ children, numColumns }: { children: React.ReactNode; numColumns: number }) {
  const theme = useFullTheme();
  return (
    <tr>
      <td colSpan={numColumns}>
        <div css={{ paddingBlock: theme.spacing[8], textAlign: 'center' }}>{children}</div>
      </td>
    </tr>
  );
}

function LoadingOverlay() {
  const theme = useFullTheme();

  return (
    <div
      css={{
        alignItems: 'center',
        // This is an rgba value. If we were to use `opacity`, it would make the spinner appear
        // transparent, but we only want the background to be transparent.
        backgroundColor: `${theme.colors.bgRegular}CC`,
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        position: 'absolute',
        top: '0',
        width: '100%',
      }}
    >
      <Spinner />
    </div>
  );
}

export type TablePaginationProps = Pick<
  PaginationProps,
  | 'currentPage'
  | 'numItems'
  | 'onPageChange'
  | 'onPageSizeChange'
  | 'pageSize'
  | 'pageSizeOptions'
  | 'variant'
>;

Table.Pagination = function TablePagination(props: TablePaginationProps) {
  return <Pagination {...props} />;
};

type NoDataProps = { children: React.ReactNode };
Table.NoData = function TableNoData({ children }: NoDataProps) {
  return <>{children}</>;
};

Table.ActionsCell = function TableActionsCell(props: ActionsCellProps) {
  return <ActionsCell {...props} />;
};

Table.BooleanCell = function TableBooleanCell(props: BooleanCellProps) {
  return <BooleanCell {...props} />;
};

Table.Cell = function TableCell(props: CellProps) {
  return <Cell {...props} />;
};

Table.CountryCell = function TableCountryCell(props: CountryCellProps) {
  return <CountryCell {...props} />;
};

Table.DateCell = function TableDateCell(props: DateCellProps) {
  return <DateCell {...props} />;
};

Table.EmailCell = function TableEmailCell(props: EmailCellProps) {
  return <EmailCell {...props} />;
};

Table.ExpandCell = function TableExpandCell(props: ExpandCellProps) {
  return <ExpandCell {...props} />;
};

Table.TextCell = function TableTextCell(props: TextCellProps) {
  return <TextCell {...props} />;
};

// Commenting out for now so we can have more discussion on https://github.com/ApplyBoard/platform_monorepo_ui/pull/5626#discussion_r1180567174
// Table.IdCell = function TableTextCell(props: IdCellProps) {
//   return <IdCell {...props} />;
// };

// Table.EmailCell = EmailCell;

function getExpandedValue(val: number[] | undefined) {
  return val
    ? val.reduce(
        (result, rowNumber) => {
          result[rowNumber] = true;
          return result;
        },
        {} as Record<string, boolean>,
      )
    : {};
}
